import React, { useState } from 'react'
import { Box } from '@mui/material'
import { Instrument, InstrumentType, LanguageCode, Song, SONG_TYPES } from '../../../../utils/types'
import BasicButton from '../../../../assets/components/BasicButton'
import Text from '../../../../assets/components/Text'
import { buttonSounds, hybridSounds, joystickSounds, touchSounds } from '../../../../utils/api'
import { Drawer } from '../../Welcome'
import AllSoundsAvailable from '../../Components/AllSoundsAvailable'
import InstrumentSettings from '../../Components/InstrumentSettings'

import { useContext } from 'react'
import { PolyglotContext } from '../../../../polyglot/polyGlotContext'
import { formatKeytone, getSongTitle } from '../../../../utils/songHelpes'
import { useCurrentUser } from '../../../../hooks/contexts/currentUserContext'
interface InstrumentInformationProps {
    instrument: Instrument
    songList: Song[] | undefined
    shouldShowSettings: boolean
    currentSong: Song | undefined
    isSmallScreen: boolean
}

const InstrumentInformation = ({ currentSong, instrument, songList }: InstrumentInformationProps) => {
    const { polyglot } = useContext(PolyglotContext)
    const currentUser = useCurrentUser()
    const lang: LanguageCode = currentUser?.customer?.userSettings?.language ?? 'sv'

    const [isOpen, setIsOpen] = useState(false)
    const currentInstrumentSong = songList?.find((song: Song) => instrument.currentSong?.id === song.id)
    const [selectedInstrument, setSelectedInstrument] = useState<Instrument>({
        deviceId: '',
        type: '',
        serialNr: '',
        color: ''
    })
    const [isAllSongsDrawerOpen, setIsAllSongsDrawerOpen] = useState(false)

    const toggleDrawer = ({ instrumentId, instrumentType, instrumentSerialNr, color }: { instrumentId?: string; instrumentType?: InstrumentType; instrumentSerialNr?: string; color?: string }) => {
        setIsOpen(!isOpen)
        setSelectedInstrument({
            color: color ?? '',
            deviceId: instrumentId ?? '',
            type: instrumentType ?? '',
            serialNr: instrumentSerialNr ?? ''
        })
    }

    const toggleAllSongsDrawer = ({ instrumentId, instrumentType, instrumentSerialNr, color }: { instrumentId?: string; instrumentType?: InstrumentType; instrumentSerialNr?: string; color?: string }) => {
        setIsAllSongsDrawerOpen(!isAllSongsDrawerOpen)
        setSelectedInstrument({
            color: color ?? '',
            deviceId: instrumentId ?? '',
            type: instrumentType ?? '',
            serialNr: instrumentSerialNr ?? ''
        })
    }

    function getInstrumentSound() {
        if (instrument.currentSong?.sound) {
            if (instrument.type === 'buttons') {
                return buttonSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
            if (instrument.type === 'joysticks') {
                return joystickSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
            if (instrument.type === 'touch') {
                return touchSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
            if (instrument.type === 'hybrid') {
                return hybridSounds.find((sound) => sound.id === instrument.currentSong?.sound)
            }
        }
        return null
    }

    const instrumentSound = getInstrumentSound()
    const soundName = instrumentSound?.name || 'Unknown sound'

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'space-between',
                justifyContent: 'space-between'
            }}
        >
            <Box sx={{ flexGrow: 0 }}>
                {currentInstrumentSong?.localizedTitle && (
                    <Box>
                        <Text size={'tiny'} bold>
                            {currentInstrumentSong.type === SONG_TYPES.MUSIC ? polyglot.t('home.instrumentInformation.paragraphMusic') : currentInstrumentSong.type === SONG_TYPES.SOUNDBANKS ? polyglot.t('home.instrumentInformation.paragraphSoundscape') : polyglot.t('home.instrumentInformation.paragraphKey')}
                        </Text>
                        <Text>{currentInstrumentSong.type === SONG_TYPES.KEY ? formatKeytone(currentInstrumentSong.key, lang) : getSongTitle(currentInstrumentSong, lang)}</Text>
                    </Box>
                )}
                {instrumentSound && (
                    <Box paddingTop="0.5rem">
                        <Text size={'tiny'} bold>
                            {polyglot.t('home.instrumentInformation.paragraphSound')}
                        </Text>
                        <Box
                            sx={{
                                display: 'flex',
                                columnGap: '0.25rem',
                                alignItems: 'center'
                            }}
                        >
                            <BasicButton
                                variant="outlined"
                                title={soundName}
                                sx={{ padding: '2px' }}
                                onClick={() =>
                                    toggleDrawer({
                                        instrumentId: instrument.deviceId,
                                        instrumentType: instrument.type,
                                        instrumentSerialNr: instrument.serialNr
                                    })
                                }
                            />
                        </Box>
                    </Box>
                )}
                {/* {currentInstrumentSong?.touchMode && currentInstrumentSong?.touchMode === "BOTH" && <TouchAlternateMode instrument={instrument} />} */}
            </Box>
            <Box sx={{ paddingTop: '2rem' }}>
                <BasicButton
                    title={polyglot.t('home.instrumentInformation.paragraphChooseSound')}
                    variant="text"
                    sx={{ padding: '2px', width: '2rem' }}
                    onClick={() =>
                        toggleAllSongsDrawer({
                            instrumentId: instrument.deviceId,
                            instrumentType: instrument.type,
                            instrumentSerialNr: instrument.serialNr
                        })
                    }
                />
                <Drawer variant="bottom" isOpen={isOpen} toggleDrawer={() => toggleDrawer({})} heading={polyglot.t('home.instrumentInformation.headingChooseSound')}>
                    <InstrumentSettings currentSong={currentSong} selectedInstrument={selectedInstrument} />
                </Drawer>
                <Drawer
                    variant="bottom"
                    isOpen={isAllSongsDrawerOpen}
                    toggleDrawer={() => toggleAllSongsDrawer({})}
                    // heading={polyglot.t("home.instrumentInformation.headingChooseSound")}
                >
                    <AllSoundsAvailable songList={songList} selectedInstrument={selectedInstrument} />
                </Drawer>
            </Box>
        </Box>
    )
}

export default InstrumentInformation
